import { useState } from "react";
import toast from "react-hot-toast";
import { Plus, Briefcase } from "lucide-react";
import { useServices, useDeleteService, RECURRENCE_LABEL, formatPrice, ServiceRecord } from "@/api/services";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import ServiceFormModal from "./ServiceFormModal";

export default function ServicesPage() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ServiceRecord | null>(null);

  const { data, isLoading } = useServices({ search: search || undefined, isActive: showInactive ? undefined : true });
  const deleteService = useDeleteService();

  const services = data?.data ?? [];

  const handleDelete = async (service: ServiceRecord) => {
    if (!window.confirm(`¿Eliminar el servicio "${service.name}" del catálogo?`)) return;
    try {
      await deleteService.mutateAsync(service.id);
      toast.success("Servicio eliminado del catálogo.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Servicios</h1>
          <p className="text-sm text-gray-500">Catálogo de servicios que ofrece la firma; los clientes los contratan desde su expediente.</p>
        </div>
        {hasPermission("services.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nuevo servicio
          </button>
        )}
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          placeholder="Buscar por nombre..."
          className="input-field min-w-[220px] flex-1"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Incluir inactivos
        </label>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Servicio</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Recurrencia</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Precio base</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && services.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                  Aún no hay servicios en el catálogo.
                </td>
              </tr>
            )}
            {services.map((service) => (
              <tr key={service.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <button
                    type="button"
                    className="flex items-center gap-2 font-medium text-gray-900 hover:text-brand-600"
                    onClick={() => hasPermission("services.update") && setEditing(service)}
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                      <Briefcase size={15} />
                    </span>
                    {service.name}
                  </button>
                  {service.description && <p className="ml-10 text-xs text-gray-400">{service.description}</p>}
                </td>
                <td className="px-4 py-3 text-gray-600">{RECURRENCE_LABEL[service.recurrence]}</td>
                <td className="px-4 py-3 text-gray-600">{formatPrice(service.defaultPrice)}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      service.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {service.isActive ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {hasPermission("services.delete") && (
                    <button type="button" className="text-xs text-gray-400 hover:text-red-600" onClick={() => handleDelete(service)}>
                      Eliminar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <ServiceFormModal onClose={() => setShowCreate(false)} />}
      {editing && <ServiceFormModal service={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
